import React, { useState } from "react";
import {useNavigate} from "react-router-dom";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useAuth } from "hooks/useAuth";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import { APIPostRegisterTeacherSchools } from "../api/login";
import {APIGetSchools} from "../api/schools";
import {useServerErrors} from "../../../hooks/useServerErrors";

interface IFormRegister {
    school: {label: string, value: number, slug: string};
    code: string;
    last_name: string;
    first_name: string;
    email: string;
    password?: string;
    confirm_password?: string;
}

const schema = yup
    .object({
    })
    .required();

const RegisterSchoolTeachersForm = () => {
    const { update } = useAuth();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState<string>("");

    const methods = useForm<IFormRegister>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormRegister>(methods.setError);

    const handleUserRegister = () => {
        update("error", {
            show: true,
            closable: true,
            title: "Cererea de înregistrare este în curs de procesare",
            message: "În curând veți primi un e-mail de verificare a contului.",
            icon: "fat face-smile",
            button: "ÎNCHIDE",
            returnFunction: () => {
                navigate("/");
            },
        });
    }

    const handleRegisterError = (err: any) => {
        if (err?.response?.status === 400) {
            const errors = err.response.data.data;
            handleServerErrors(errors);
        } else {
            update("error", {show: true, code: "A001"});
        }
    }

    const onSubmit: SubmitHandler<IFormRegister> = (data) => {
        const school = data.school?.slug || null;

        update("loading", { show: true, modal: true });
        APIPostRegisterTeacherSchools({
            school: school,
            code: data.code,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            password: data.password,
            confirm_password: data.confirm_password,
        })
            .then((response) => {
                if (response.data.success) {
                    setErrorMessage("");
                    handleUserRegister();
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(handleRegisterError)
            .finally(() => update("loading", {show: false}));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                <Box mb={2}>
                    <Box py={1}>
                        <Typography sx={{color: "primary.dark", fontSize: "1.1rem",}}>Caută școala ta</Typography>
                        <FormField
                            name="school"
                            type="autocomplete_api"
                            label="Caută școala..."
                            optionsAPI={APIGetSchools}
                            defaultValue={null}
                            multiple={false}
                            filterOptions={(x: any) => x}
                            errorMessage={methods.formState.errors.school?.message}
                            error={!!methods.formState.errors.school?.message}
                            autoFocus={true}
                        />
                    </Box>
                    <Box py={1}>
                        <Typography sx={{color: "primary.dark", fontSize: "1.1rem",}}>
                            Introdu codul primit sau serial number al dispozitivului (regăsit pe lateralul echipamentului)
                        </Typography>
                        <FormField
                            name="code"
                            type="text"
                            defaultValue=""
                            label="Cod"
                            error={!!methods.formState.errors.code?.message}
                            errorMessage={methods.formState.errors.code?.message}
                        />
                    </Box>
                </Box>
                <Divider sx={{ borderColor: "primary.main", marginBottom: 1.5, marginTop: 1 }} />
                <Box py={1}>
                    <FormField
                        name="first_name"
                        type="text"
                        defaultValue=""
                        label="Prenume"
                        error={!!methods.formState.errors.first_name?.message}
                        errorMessage={methods.formState.errors.first_name?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="last_name"
                        type="text"
                        defaultValue=""
                        label="Nume"
                        error={!!methods.formState.errors.last_name?.message}
                        errorMessage={methods.formState.errors.last_name?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="email"
                        type="text"
                        defaultValue=""
                        label="Adresa de e-mail"
                        autoComplete="email"
                        error={!!methods.formState.errors.email?.message}
                        errorMessage={methods.formState.errors.email?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="password"
                        type="password"
                        defaultValue=""
                        label="Parola"
                        autoComplete="password"
                        error={!!methods.formState.errors.password?.message}
                        errorMessage={methods.formState.errors.password?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="confirm_password"
                        type="password"
                        label="Confirmă parola"
                        error={!!methods.formState.errors.confirm_password?.message}
                        errorMessage={methods.formState.errors.confirm_password?.message}
                    />
                </Box>
                <Box sx={{mt: 1, mb: 3}}>
                    <Button type="submit" variant="contained">Înregistrare</Button>
                    <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                </Box>
            </form>
        </FormProvider>
    );
};

export default RegisterSchoolTeachersForm;
